import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  getProgramWorksheetRegisteredPages,
} from '../../services/programWorksheetRegisteredPagesService';
import type { ProgramWorksheetRegisteredPage } from '../../utils/programWorksheetRegisteredPages';
import { PROGRAM_WORKSHEET_REGISTERED_PAGES_CHANGED_EVENT } from '../../utils/programWorksheetRegisteredPages';

const PICKER_Z = 12080;

const LearningContentWorksheetTemplatePickerModal: React.FC<{
  open: boolean;
  workspaceId: string;
  onClose: () => void;
  onPick: (page: ProgramWorksheetRegisteredPage) => void;
}> = ({ open, workspaceId, onClose, onPick }) => {
  const [pages, setPages] = useState<ProgramWorksheetRegisteredPage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const sid = workspaceId.trim();
    if (!sid) {
      setPages([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    void getProgramWorksheetRegisteredPages(sid, { forceRefresh: true })
      .then((list) => {
        if (!cancelled) setPages(list);
      })
      .catch(() => {
        if (!cancelled) setPages([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    const onChanged = (e: Event) => {
      const d = (e as CustomEvent<{ workspaceId?: string }>).detail;
      if ((d?.workspaceId ?? '').trim() !== sid) return;
      void getProgramWorksheetRegisteredPages(sid).then((list) => {
        if (!cancelled) setPages(list);
      });
    };
    window.addEventListener(PROGRAM_WORKSHEET_REGISTERED_PAGES_CHANGED_EVENT, onChanged as EventListener);
    return () => {
      cancelled = true;
      window.removeEventListener(
        PROGRAM_WORKSHEET_REGISTERED_PAGES_CHANGED_EVENT,
        onChanged as EventListener
      );
    };
  }, [open, workspaceId]);

  if (!open) return null;

  return createPortal(
    <div
      className="cp-modal-backdrop cp-learning-worksheet-template-picker-backdrop"
      style={{ zIndex: PICKER_Z }}
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        className="cp-modal cp-learning-worksheet-template-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-learning-worksheet-template-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="cp-learning-worksheet-template-picker-title">활동지 레이아웃 선택</h3>
        <div className="cp-modal-body">
          {loading ? (
            <p className="cp-learning-worksheet-template-picker-empty">불러오는 중…</p>
          ) : pages.length === 0 ? (
            <p className="cp-learning-worksheet-template-picker-empty">
              등록된 활동지 레이아웃이 없습니다. 계정 메뉴에서 「활동지 레이아웃 등록」으로 추가해 주세요.
            </p>
          ) : (
            <ul className="cp-learning-worksheet-template-picker-list">
              {pages.map((page) => (
                <li key={page.id}>
                  <button
                    type="button"
                    className="cp-learning-worksheet-template-picker-item"
                    onClick={() => onPick(page)}
                  >
                    <span className="cp-learning-worksheet-template-picker-item-name">{page.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LearningContentWorksheetTemplatePickerModal;
